/**
 * `/browse/artists` — TASK 8.1 Step 1.
 *
 * The mock's `_browse(V,'artists')` view model (L1032-1035) and its template
 * (L214-236), wired to `GET /api/artists` (TASK 4.1) instead of the in-memory
 * `db.artists`. What changed and why:
 *
 *   mock `this.db.artists.slice(0,n)` -> one cursor PAGE at a time. The bench
 *       profile is 2,000+ artists, the endpoint is keyset-paginated
 *       (`dto.ts#cursorPageQuerySchema`), and ui-contract §10.6 specifies the
 *       affordance exactly: initial window 40, "Load more artists ↓", +40.
 *   mock `state.artShown` + `moreArtists` -> ../../components/VirtualList.js,
 *       which owns BOTH bounds (render window and fetch) so no view has to.
 *   mock's shared `us` block -> ../../components/StateBlocks.js, rendered into
 *       the section BODY rather than instead of the page, so the heading and
 *       the tab strip survive a failed load (§10.6: a failed section never
 *       forces a whole-page error).
 *
 * The GENRES tab is a link only. `/browse/genres` is TASK 4.3's surface and is
 * not built here; the tab must still be present and correctly `aria-selected`
 * because ui-contract §3 tabulates both on this route.
 *
 * @module
 */

/* global AbortController -- browser platform API; eslint.config.js's browser
   globals block predates the views using it and is outside TASK 8.1's files */

import { TESTIDS, artistRowLink } from '@streamymusic/shared';

import { VirtualList } from '../../components/VirtualList.js';
import {
  EmptyBlock,
  ErrorBlock,
  LIBRARY_PREPARING_MESSAGE,
  LoadingBlock,
} from '../../components/StateBlocks.js';
import { append, el } from '../../components/primitives/dom.js';

import { BrowseTabs } from './BrowseTabs.js';
import { artistImageUrl, gradientFor, initialsOf } from '../../components/primitives/format.js';

/** ui-contract §10.6 scale table — /browse/artists renders 40, then +40. */
export const ARTISTS_PAGE_SIZE = 40;

/**
 * One row of the A-Z list (mock L225-229).
 *
 * @param {import('@streamymusic/shared').ArtistListItem} artist
 * @returns {HTMLElement}
 */
export function ArtistRow(artist) {
  const row = el('a', {
    className: 'sm-row',
    testId: artistRowLink(artist.id),
    attrs: { href: `#/artists/${encodeURIComponent(artist.id)}` },
  });
  const thumb = el('span', {
    className: 'sm-thumb sm-thumb--circle',
    text: initialsOf(artist.name),
    styleProps: { background: gradientFor(artist.id) },
    attrs: { 'aria-hidden': 'true' },
  });
  // Real artist photo layered OVER the gradient + initials — the SAME approach
  // as the album cards' `.sm-cover__art-img` (primitives/Card.js). Built only
  // when the artist has a real `artists/…` ref (format.js `artistImageUrl`);
  // geometry (absolute, fill, object-fit:cover, inherited radius) is
  // `.sm-thumb__img` in components.css. On a load error the img removes
  // itself, revealing the gradient + initials — so a broken/404 photo never
  // leaves an artist worse off than the photo-less look.
  const photoUrl = artistImageUrl(artist);
  if (photoUrl !== undefined) {
    const img = el('img', {
      className: 'sm-thumb__img',
      attrs: { src: photoUrl, alt: '', loading: 'lazy', decoding: 'async' },
      on: { error: () => img.remove() },
    });
    append(thumb, img);
  }
  append(
    row,
    thumb,
    el('span', { className: 'sm-line-name', text: artist.name }),
    el('span', {
      className: 'sm-line-trailing',
      text: `${artist.albumCount.toLocaleString()} album${artist.albumCount === 1 ? '' : 's'}`,
    }),
  );
  return row;
}

/**
 * Builds the `/browse/artists` section and starts loading its first page.
 *
 * @param {{
 *   api: Pick<import('../../../api/api-client.js').ApiClient, 'getArtists'>,
 *   heading: HTMLElement,
 *   isAdmin?: boolean,
 *   pageSize?: number,
 *   depth?: import('../../../lib/list-depth.js').ListDepth,
 *   announce?: (text: string) => void,
 * }} options
 *   `heading` is the route's `<h1 data-testid="page-heading">` (router.js
 *   `PageHeading`) — the shell contract requires exactly one per route and the
 *   route announcer focuses it, so the view is handed it rather than making
 *   its own. `announce` is the shell's persistent polite live region
 *   (LiveRegions.js) — see StateBlocks.js#ErrorBlock for why the blocks
 *   cannot announce themselves. `depth` is the #back seam: absent, the list
 *   behaves exactly as before (one page, default window); present, it restores
 *   the depth the user left — see `lib/list-depth.ts`.
 * @returns {{ element: HTMLElement, dispose: () => void }} the `<section>` for
 *   `Shell#renderRoute`, plus the cancel hook the composition root calls on
 *   the next navigation
 */
export function ArtistsList(options) {
  const { api, heading, isAdmin = false, pageSize = ARTISTS_PAGE_SIZE, depth, announce } = options;

  const section = el('section', { attrs: { 'data-screen-label': 'Browse' } });

  const tabs = BrowseTabs('artists');

  const countLabel = el('p', { className: 'sm-count-label', attrs: { hidden: true } });
  const body = el('div');
  /** Holds an error for a FAILED "load more" — the loaded rows must survive it. */
  const notice = el('div');
  append(section, heading, tabs, countLabel, body, notice);

  /** @type {import('@streamymusic/shared').ArtistListItem[]} */
  let items = [];
  /** @type {string|null} */
  let cursor = null;
  /**
   * Guards against an out-of-order settle: a Retry issued while the previous
   * request is still in flight would otherwise paint the older answer.
   */
  let generation = 0;
  let disposed = false;
  /** Cancels the in-flight page fetch on dispose (and on a superseding load). */
  /** @type {AbortController|null} */
  let controller = null;
  /** Pages held, so the depth memory can replay them on return (#back). */
  let pagesLoaded = 0;

  /** The depth this route was left at, read ONCE before the first paint. */
  const restored = depth?.read() ?? null;

  const list = VirtualList({
    items,
    windowSize: pageSize,
    renderItem: (artist) => ArtistRow(artist),
    loadMoreLabel: 'Load more artists ↓',
    loadMoreTestId: TESTIDS.LOAD_MORE_ARTISTS_BUTTON,
    listClassName: 'sm-stack-8',
    ...(restored === null ? {} : { initialShown: restored.shown }),
    onWindowChange: (shown) => depth?.remember(pagesLoaded, shown),
    onLoadMore: () => {
      void load({ append: true });
    },
  });

  /**
   * The count line. The endpoint returns no TOTAL (it is a keyset page, not an
   * offset page), so the honest label is what we hold — with a "+" while the
   * server says there is more, rather than the mock's `db.artists.length`,
   * which no longer exists client-side.
   */
  function paintCount() {
    const n = items.length;
    countLabel.textContent = `${n.toLocaleString()}${cursor === null ? '' : '+'} artist${n === 1 ? '' : 's'}`;
    countLabel.removeAttribute('hidden');
  }

  /**
   * @param {{ append?: boolean }} [opts]
   * @returns {Promise<void>}
   */
  async function load(opts = {}) {
    const appendPage = opts.append === true;
    const own = (generation += 1);
    // A superseded request is CANCELLED, not merely ignored: the generation
    // guard already discards its settle, aborting stops the transfer too.
    controller?.abort();
    const ownController = new AbortController();
    controller = ownController;

    notice.replaceChildren();
    if (!appendPage) {
      items = [];
      cursor = null;
      pagesLoaded = 0;
      countLabel.setAttribute('hidden', '');
      body.replaceChildren(LoadingBlock({ count: 6, label: 'Loading artists' }));
    }

    try {
      const page = await api.getArtists({
        limit: pageSize,
        ...(appendPage && cursor !== null ? { cursor } : {}),
        signal: ownController.signal,
      });
      if (disposed || own !== generation) return;

      items = appendPage ? [...items, ...page.items] : [...page.items];
      cursor = page.nextCursor;
      pagesLoaded += 1;

      if (items.length === 0) {
        body.replaceChildren(
          EmptyBlock({
            message: LIBRARY_PREPARING_MESSAGE,
            showSyncLink: isAdmin,
            ...(announce === undefined ? {} : { announce }),
          }),
        );
        countLabel.setAttribute('hidden', '');
        return;
      }

      paintCount();
      list.update({ items, hasMore: cursor !== null });
      if (body.firstChild !== list.element) body.replaceChildren(list.element);
      depth?.remember(pagesLoaded, list.shownCount());
    } catch {
      if (disposed || own !== generation) return;
      // The api-client has already collapsed the failure to a SAFE message and
      // handled 401/403 globally; a browse read has nothing user-specific to
      // add, so this is the shared retriable card.
      const retry = ErrorBlock({
        onRetry: () => {
          void load(appendPage ? { append: true } : {});
        },
        ...(announce === undefined ? {} : { announce }),
      });
      if (appendPage) {
        // A failed NEXT page must not discard the pages already on screen.
        list.update({ busy: false });
        notice.replaceChildren(retry);
      } else {
        body.replaceChildren(retry);
      }
    }
  }

  /**
   * First paint. With a remembered depth (#back) the pages the user had already
   * fetched are re-read BEFORE the list settles — every one a hit on the 24h
   * catalog cache the api-client keeps per cursor, so this costs no network and
   * no spinner, and it rebuilds the tall document the saved scroll position
   * belongs to. Stops early on dispose, on a superseding load, or when the
   * server runs out of pages.
   *
   * @returns {Promise<void>}
   */
  async function loadToRememberedDepth() {
    await load();
    if (restored === null) return;
    for (let page = 1; page < restored.pages; page += 1) {
      if (disposed || cursor === null) return;
      const before = items.length;
      await load({ append: true });
      // A page that added nothing (or failed) means the replay is done; another
      // turn of the loop would spin on the same cursor.
      if (items.length === before) return;
    }
  }

  void loadToRememberedDepth();

  return {
    element: section,
    dispose() {
      // Cancels every in-flight paint: the next settle sees `disposed` and
      // writes nothing into a section the shell has already replaced. The
      // abort additionally cancels the TRANSFER — no orphaned fetch keeps
      // downloading a page nobody will render.
      disposed = true;
      controller?.abort();
      list.dispose();
    },
  };
}
